import { Spine } from "@esotericsoftware/spine-pixi-v8";
import type { HeroState } from "../helpers/types";
import { Container } from "pixi.js";
import { sounds } from "@/utils/Sounds";
import { heroesData } from "@/settings/heroesData";

const MS_PER_SECOND = 1000;
const COLOR_ANIMATION_TRACK = 10;
const RUN_VARIANTS = ['random1_loop_50', 'random2_loop_25'] as const;

export class HeroController extends Container {
    #state: HeroState = 'idle';
    private animationActive = false;

    constructor(
        public id: string,
        private spine: Spine
    ) {
        super();

        this.addChild(spine);
        this.playStateAnimation();
    }

    set state(state: HeroState) {
        if (this.#state === state) {
            return;
        }

        this.#state = state;

        this.playStateAnimation();
    }

    get state(): HeroState {
        return this.#state;
    }

    set color(color: 'white' | 'yellow' | 'green') {
        this.playAnimation(color, COLOR_ANIMATION_TRACK);
    }

    private playStateAnimation() {
        switch (this.#state) {
            case 'idle':
                this.playAnimation(`hero_idle_loop`);
                break;
            case 'run': {
                this.playAnimation(
                    `hero_run_random/${RUN_VARIANTS[Math.floor(Math.random() * RUN_VARIANTS.length)]}`
                );
                break;
            }
            case 'win':
                sounds.playFX(`sounds/words/${this.name}/win`);
                this.playAnimation(`hero_win_next_hero_run`);
                break;
        }
    }

    private async playAnimation(name: string, track = 0) {
        if (this.animationActive) {
            return;
        }

        this.spine.state.setAnimation(track, name, name.includes('_loop'));

        const animationData = this.spine?.state.data.skeletonData.findAnimation(name);

        return new Promise<void>((resolve) => {
            if (animationData) {
                const durationInSeconds = animationData.duration;

                setTimeout(() => {
                    this.animationActive = false;

                    resolve();
                }, durationInSeconds * MS_PER_SECOND);
            } else {
                this.animationActive = false;

                resolve();
            }
        });
    }

    async playSelectedAnimation() {
        await this.playAnimation(`hero_selected_next_hero_idle`);
        this.playStateAnimation();
    }

    async speedUp() {
        sounds.playFX(`sounds/words/${this.name}/run`);
        await this.playAnimation(`hero_boost_next_hero_run`);
        this.playStateAnimation();
    }

    async slowDown() {
        await this.playAnimation(`hero_brake_next_hero_run`);
        this.playStateAnimation();
    }

    moveTo(x: number) {
        this.x = x;
    }

    get name(): string {
        return heroesData[this.id].name;
    }
}
